import type { ApiErrorBody, AuthResult } from './types'

/** Thrown for every non-2xx response so callers can branch on `code`. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, unknown>

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isOutOfCredits(): boolean {
    return this.status === 402 || this.code === 'INSUFFICIENT_CREDITS'
  }

  get isNotImplemented(): boolean {
    return this.status === 501 || this.code === 'NOT_IMPLEMENTED'
  }
}

const ACCESS_KEY = 'll.accessToken'
const REFRESH_KEY = 'll.refreshToken'
const EXPIRES_KEY = 'll.expiresAt'

export interface StoredTokens {
  accessToken: string
  refreshToken: string
  expiresAt: string
}

export const tokenStore = {
  read(): StoredTokens | null {
    const accessToken = localStorage.getItem(ACCESS_KEY)
    const refreshToken = localStorage.getItem(REFRESH_KEY)
    const expiresAt = localStorage.getItem(EXPIRES_KEY)
    if (!accessToken || !refreshToken || !expiresAt) return null
    return { accessToken, refreshToken, expiresAt }
  },
  write(tokens: StoredTokens): void {
    localStorage.setItem(ACCESS_KEY, tokens.accessToken)
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken)
    localStorage.setItem(EXPIRES_KEY, tokens.expiresAt)
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(EXPIRES_KEY)
  },
}

/** Called when refresh fails, so the auth context can drop the session. */
let onSessionLost: (() => void) | null = null
export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler
}

const BASE = (import.meta.env.VITE_API_BASE ?? '/api').replace(/\/$/, '')

async function readError(response: Response): Promise<ApiError> {
  let code = `HTTP_${response.status}`
  let message = response.statusText || 'Request failed'
  let details: Record<string, unknown> | undefined

  try {
    const body = (await response.json()) as Partial<ApiErrorBody>
    if (body?.error) {
      code = body.error.code || code
      message = body.error.message || message
      details = body.error.details
    }
  } catch {
    // A proxy or a 502 can return HTML; the status-derived defaults stand.
  }

  return new ApiError(response.status, code, message, details)
}

/** In-flight refresh, shared so N parallel 401s trigger exactly one refresh. */
let refreshInFlight: Promise<StoredTokens | null> | null = null

async function refreshTokens(): Promise<StoredTokens | null> {
  const current = tokenStore.read()
  if (!current) return null

  const response = await fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: current.refreshToken }),
  })

  if (!response.ok) {
    tokenStore.clear()
    onSessionLost?.()
    return null
  }

  const result = (await response.json()) as AuthResult
  const next: StoredTokens = {
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    expiresAt: result.expiresAt,
  }
  tokenStore.write(next)
  return next
}

function ensureRefresh(): Promise<StoredTokens | null> {
  refreshInFlight ??= refreshTokens().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

export interface RequestOptions {
  method?: string
  body?: unknown
  /** Skips the Authorization header — used by login/register/refresh. */
  anonymous?: boolean
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${BASE}${path.startsWith('/') ? path : `/${path}`}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function send(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const headers = new Headers()
  const isFormData = options.body instanceof FormData
  if (options.body !== undefined && !isFormData) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const init: RequestInit = { method: options.method ?? 'GET', headers }
  if (options.signal) init.signal = options.signal
  if (options.body !== undefined) {
    init.body = isFormData ? (options.body as FormData) : JSON.stringify(options.body)
  }

  return fetch(buildUrl(path, options.query), init)
}

/**
 * Core request. On a 401 with a stored refresh token it refreshes once and
 * replays the original request; a second 401 means the session is really gone.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = options.anonymous ? null : (tokenStore.read()?.accessToken ?? null)
  let response = await send(path, options, token)

  if (response.status === 401 && !options.anonymous && tokenStore.read()) {
    const refreshed = await ensureRefresh()
    if (refreshed) {
      response = await send(path, options, refreshed.accessToken)
    }
  }

  if (response.status === 401 && !options.anonymous) {
    tokenStore.clear()
    onSessionLost?.()
  }

  if (!response.ok) throw await readError(response)

  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T
  }

  const contentType = response.headers.get('Content-Type') ?? ''
  if (!contentType.includes('json')) return undefined as T

  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

/**
 * Absolute URL for endpoints the browser downloads directly. Prefer
 * {@link apiDownload} in an authenticated app: a bare <a href> reports no
 * Authorization header, so it 401s on any [Authorize] endpoint.
 */
export function apiUrl(path: string): string {
  return buildUrl(path, undefined)
}

/**
 * Fetches a file endpoint through the authenticated client and hands the browser
 * a Blob to save. This is how a download link must fetch from an [Authorize]
 * endpoint: the request carries the bearer token (and the 401 refresh logic),
 * which a plain navigation never does. Throws ApiError on non-2xx like every
 * other call, so the caller can show the real reason instead of a dead download.
 *
 * `fileName` may be null, in which case the server's Content-Disposition wins.
 * That is the right default for a result file: the server knows the entity's
 * label and when the job ran, and duplicating that naming rule here would give
 * two places to keep in step. The header is reachable cross-origin because
 * CorsSetup exposes it.
 */
export async function apiDownload(
  path: string,
  fileName: string | null,
  options: RequestOptions = {},
): Promise<void> {
  const token = options.anonymous ? null : (tokenStore.read()?.accessToken ?? null)
  let response = await send(path, { ...options, method: 'GET' }, token)

  // Mirrors request(): a 401 with a stored refresh token refreshes once and
  // replays; a second 401 means the session is genuinely gone.
  if (response.status === 401 && !options.anonymous && tokenStore.read()) {
    const refreshed = await ensureRefresh()
    if (refreshed) {
      response = await send(path, { ...options, method: 'GET' }, refreshed.accessToken)
    }
  }

  if (response.status === 401 && !options.anonymous) {
    tokenStore.clear()
    onSessionLost?.()
  }

  if (!response.ok) throw await readError(response)

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = fileName ?? dispositionFileName(response, path)
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    // Give the browser a beat to begin the save before revoking the URL.
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
}

/**
 * The name the server put in Content-Disposition, falling back to the last path
 * segment. Both forms of the header are handled: RFC 5987's `filename*`, which
 * carries non-ASCII names percent-encoded, and the plain `filename=` that
 * ASP.NET Core emits for an ASCII-safe name.
 */
function dispositionFileName(response: Response, path: string): string {
  const header = response.headers.get('Content-Disposition') ?? ''

  const extended = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1])
    } catch {
      // A malformed encoding is not worth failing the download over.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header)
  if (plain?.[1]) return plain[1]

  return path.split('/').pop() || 'download'
}
